// The ED board app (milestone L5.2): sign-in, the live board, a patient page and a guide. The
// server decides who may see what (can()); the page shows what it is sent.

import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { fetchMe, SignedOut } from './api.ts';
import { NO_FILTERS, type Filters } from './board-state.ts';
import { notificationsFrom } from './notifications.ts';
import { ACKNOWLEDGE_ROLE, Layout } from './components/Layout.tsx';
import { BoardPage } from './pages/BoardPage.tsx';
import { HelpPage } from './pages/HelpPage.tsx';
import { PatientPage } from './pages/PatientPage.tsx';
import { ScenarioPage } from './pages/ScenarioPage.tsx';
import { NoAccessPage, SignInPage } from './pages/SignInPage.tsx';
import { useRoute } from './router.ts';
import { useBoardStream } from './use-board-stream.ts';

const BOARD_ROLES = ['ed_clinician', 'ed_coordinator'];

/** The clock for display, ticking every second. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

export function App() {
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, retry: false });
  if (me.isPending) return <p className="loading">Loading…</p>;
  if (me.error instanceof SignedOut) return <SignInPage />;
  if (me.error !== null)
    return <p className="loading">The board is not available: {me.error.message}</p>;
  const site = me.data.sites[0];
  if (site === undefined || !me.data.roles.some((r) => BOARD_ROLES.includes(r)))
    return <NoAccessPage />;
  return <SignedIn me={me.data} siteId={site} />;
}

function SignedIn({ me, siteId }: { me: Parameters<typeof Layout>[0]['me']; siteId: string }) {
  const route = useRoute();
  const { rows, status } = useBoardStream(siteId);
  const now = useNow();
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const notifications = useMemo(() => notificationsFrom(rows.values()), [rows]);
  return (
    <Layout
      me={me}
      route={route}
      notifications={notifications}
      siteId={siteId}
      query={filters.query}
      onQuery={(query) => setFilters((f) => ({ ...f, query }))}
    >
      {route.page === 'board' && (
        <BoardPage
          siteId={siteId}
          rows={rows}
          status={status}
          now={now}
          filters={filters}
          onFilters={setFilters}
        />
      )}
      {route.page === 'patient' && (
        <PatientPage
          siteId={siteId}
          encounterId={route.encounterId}
          boardRow={rows.get(route.encounterId)}
          now={now}
          canAcknowledge={me.roles.includes(ACKNOWLEDGE_ROLE)}
        />
      )}
      {route.page === 'help' && <HelpPage />}
      {route.page === 'demo' && <ScenarioPage />}
    </Layout>
  );
}
